/*
 * Copyright (C) 2023 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { ApiRootResource } from '@common/api-root-shared';
import { BinaryFileListResource } from '@common/binary-file-shared';
import {
  BlobWithFileName,
  buildBlobWithFileName,
  CatchHttpError,
  ContentType,
  EMPTY_STRING,
  HttpHeader,
  isNotUndefined,
  RequestOptions,
  SkipInterceptor,
} from '@common/tech-shared';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { getUrl, Resource, ResourceFactory, ResourceUri } from '@ngxp/rest';
import { map, Observable } from 'rxjs';
import { VorgangListLinkRel, VorgangWithEingangLinkRel } from '../vorgang.linkrel';
import { VorgangListResource, VorgangWithEingangResource } from '../vorgang.model';

@Injectable({ providedIn: 'root' })
export class VorgangRepository {
  private readonly resourceFactory = inject(ResourceFactory);
  private readonly httpClient = inject(HttpClient);

  static readonly SEARCH_PARAM: string = 'searchBy';
  static readonly LIMIT_PARAM: string = 'limit';
  static readonly GET_VORGANG: string = 'getVorgang';

  public loadVorgangList(apiRootResource: ApiRootResource, linkRel: string, page: number): Observable<VorgangListResource> {
    const url: URL = this._getUrlWithoutParameter(apiRootResource, linkRel);
    if (page) url.searchParams.set('page', page.toString());
    return this.resourceFactory.fromId(url.href).get();
  }

  public getNextVorgangListPage(vorgangListResource: VorgangListResource): Observable<VorgangListResource> {
    return this.resourceFactory.from(vorgangListResource).get(VorgangListLinkRel.NEXT);
  }

  public getPrevVorgangListPage(vorgangListResource: VorgangListResource): Observable<VorgangListResource> {
    return this.resourceFactory.from(vorgangListResource).get(VorgangListLinkRel.PREV);
  }

  @CatchHttpError(VorgangRepository.GET_VORGANG)
  public getVorgang(vorgangWithEingangUrl: ResourceUri): Observable<VorgangWithEingangResource> {
    return this.resourceFactory.fromId(vorgangWithEingangUrl).get();
  }

  public getAttachments(vorgang: VorgangWithEingangResource): Observable<BinaryFileListResource> {
    return this.resourceFactory.from(vorgang).get(VorgangWithEingangLinkRel.ATTACHMENTS);
  }

  @SkipInterceptor()
  public searchVorgaengeBy(
    apiRootResource: ApiRootResource,
    searchBy: string,
    linkRel: string,
    limit?: number,
  ): Observable<VorgangListResource> {
    return this.resourceFactory.fromId(this.buildSearchByUrl(apiRootResource, searchBy, linkRel, limit)).get();
  }

  buildSearchByUrl(apiRootResource: ApiRootResource, searchBy: string, linkRel: string, limit?: number): ResourceUri {
    //TODO sollte mal bei ngxp eingebaut werden und geprüft werden, dass nur erwartete Paramter befüllt werden
    const url: URL = this._getUrlWithoutParameter(apiRootResource, linkRel);
    url.searchParams.set(VorgangRepository.SEARCH_PARAM, searchBy);
    if (isNotUndefined(limit)) url.searchParams.set(VorgangRepository.LIMIT_PARAM, limit.toString());
    return url.href;
  }

  _getUrlWithoutParameter(resource: Resource, linkRel: string): URL {
    return new URL(getUrl(resource, linkRel).replace(/{&[\w,]*}$/, EMPTY_STRING));
  }

  public exportVorgang(
    vorgangWithEingang: VorgangWithEingangResource,
    linkRel: VorgangWithEingangLinkRel,
  ): Observable<BlobWithFileName> {
    return this.httpClient
      .get<HttpResponse<Blob>>(getUrl(vorgangWithEingang, linkRel), this._buildRequestOptions())
      .pipe(map(buildBlobWithFileName));
  }

  _buildRequestOptions(): RequestOptions {
    let headers = new HttpHeaders();
    headers = headers.set(HttpHeader.ACCEPT, ContentType.APPLICATION_OCTET_STREAM);
    return <RequestOptions>{ headers, responseType: 'blob' as 'json', observe: 'response' };
  }
}
